// list-controller.ts — geteilte Listen-Zustandsmaschine (PFS-632, Epic PFS-591/627).
//
// Client-Spiegel von pkg/storage/list.ListParams: EIN Controller kapselt den
// Fetch/Suche/Filter/Sort/Offset-Paging/Infinite-Scroll-Lebenszyklus,
// den heute jede Cut-App-Liste (admin-users, practices, specialties, members …)
// einzeln handverdrahtet — inklusive des Race-Token-Schutzes, den bislang nur
// admin-users hatte (die anderen konnten langsame/schnelle Antworten vertauschen).
//
// Design „Nähte statt Flags": uniformer Kern (diese Zustandsmaschine) + EINE
// benannte Naht — der `fetcher`. WIE geladen wird (URL, gRPC-Merge, Transform)
// lebt im fetcher; Sonderfälle KOMPONIEREN (wrap/extend), nie ein Config-Flag.
//
// View-Kontrakt (z.B. <pfs-paginator> ODER <pfs-data-table>-Infinite-Scroll):
//   Der View LIEST abgeleiteten Zustand (items/total/currentPage/pageCount/
//   hasMore/hasNext/loading) und RUFT Ops (goToPage/nextPage/loadMore).
//   Beide Optiken binden DENSELBEN Controller → Autoscroll <-> Paginationsleiste
//   ist ein View-Tausch, KEINE Fetch-Umverdrahtung.
//
// Offset-nativ im Kern (spiegelt list.Pagination), Seiten nur als abgeleitete
// Sicht (currentPage/pageCount) für den Paginator.
import { logger } from "../utils/debug-logger.js";

export interface SortKey {
  field: string;
  desc?: boolean;
}

/** Was der fetcher bekommt — Spiegel von list.RESTQuery + generischem Filter F. */
export interface ListParams<F = Record<string, unknown>> {
  limit: number;
  offset: number;
  search: string;
  sort: SortKey[];
  filters: F;
}

/** Was der fetcher zurueckgibt — Spiegel von list.Page[T]. */
export interface ListPage<T> {
  items: T[];
  total: number;
}

export type ListFetcher<T, F> = (params: ListParams<F>) => Promise<ListPage<T>>;

/** Zentraler REST-Cap fuer die Seitengroesse — Spiegel von pkg/storage/list.RESTQuery
 *  (query.limit maximum:100, PFS-627). Der ListController clampt jede `limit` darauf,
 *  damit keine Liste den Backend-Cap ueberschreitet (sonst 422). Endpunkte, die mehr
 *  erlauben, heben es per opts.restMaxLimit an. */
export const LIST_MAX_LIMIT = 100;

export interface ListControllerOptions<T, F> {
  /** Die EINE Naht: wie eine Seite geladen wird. */
  fetcher: ListFetcher<T, F>;
  /** Default-Seitengroesse (REST-Default 25). Wird auf restMaxLimit geclampt. */
  limit?: number;
  /** Zentraler REST-Cap der Seitengroesse (Default LIST_MAX_LIMIT=100). Nur fuer
   *  Endpunkte anheben, die nachweislich mehr erlauben. */
  restMaxLimit?: number;
  sort?: SortKey[];
  filters?: F;
}

export class ListController<T = unknown, F = Record<string, unknown>> {
  private readonly fetcher: ListFetcher<T, F>;
  private readonly restMaxLimit: number;
  private readonly initialFilters: F;

  private _items: T[] = []; // die aktuell ANGEZEIGTEN Zeilen (Paginator-Fenster ODER Endless-Akkumulat)
  private _total = 0;
  private _limit: number;
  private _offset = 0;
  private _search = "";
  private _sort: SortKey[];
  private _filters: F;
  private _loading = false;
  private _loadingMore = false;
  private _error: unknown = null;
  // Race-Token (generalisiert admin-users' loadSeq): jeder load/loadMore
  // bindet an seine Sequenz; eine neuere Sequenz verwirft veraltete Antworten.
  private seq = 0;

  private readonly listeners = new Set<(c: ListController<T, F>) => void>();

  constructor(opts: ListControllerOptions<T, F>) {
    this.fetcher = opts.fetcher;
    this.restMaxLimit = Math.max(1, opts.restMaxLimit ?? LIST_MAX_LIMIT);
    this._limit = this.clampLimit(opts.limit ?? 25);
    this._sort = opts.sort ?? [];
    this.initialFilters = opts.filters ?? ({} as F);
    this._filters = this.initialFilters;
  }

  // --- abgeleiteter Zustand (der View liest ihn) ---
  get items(): readonly T[] {
    return this._items;
  }
  get total(): number {
    return this._total;
  }
  get limit(): number {
    return this._limit;
  }
  get offset(): number {
    return this._offset;
  }
  get search(): string {
    return this._search;
  }
  get sort(): readonly SortKey[] {
    return this._sort;
  }
  get filters(): F {
    return this._filters;
  }
  get loading(): boolean {
    return this._loading;
  }
  get loadingMore(): boolean {
    return this._loadingMore;
  }
  get error(): unknown {
    return this._error;
  }

  get pageCount(): number {
    return this._limit > 0 ? Math.max(1, Math.ceil(this._total / this._limit)) : 1;
  }
  get currentPage(): number {
    return this._limit > 0 ? Math.floor(this._offset / this._limit) + 1 : 1;
  }
  get hasPrev(): boolean {
    return this._offset > 0;
  }
  get hasNext(): boolean {
    return this.currentPage < this.pageCount;
  }
  /** Infinite-Scroll: gibt es Zeilen jenseits der bereits geladenen? */
  get hasMore(): boolean {
    return this._items.length < this._total;
  }

  // --- Subscription: der View re-rendert im Callback ---
  subscribe(fn: (c: ListController<T, F>) => void): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }
  private emit(): void {
    for (const fn of this.listeners) fn(this);
  }

  private params(offset = this._offset, limit = this._limit): ListParams<F> {
    return { limit, offset, search: this._search, sort: this._sort, filters: this._filters };
  }

  // --- Lade-Ops ---

  /** Laedt die Seite am aktuellen offset (REPLACE) — jede Navigation frisch vom
   *  Server. Basis fuer Paginator + Initial. */
  async load(): Promise<void> {
    const s = ++this.seq;
    this._loading = true;
    this._error = null;
    this.emit();
    try {
      const page = await this.fetcher(this.params());
      if (s !== this.seq) return; // Filter/Suche wechselte → veraltete Antwort verwerfen
      this._items = page.items;
      this._total = page.total;
    } catch (e) {
      if (s !== this.seq) return;
      this._error = e;
      logger.warn("ListController.load failed", e);
    } finally {
      if (s === this.seq) {
        this._loading = false;
        this.emit();
      }
    }
  }

  /** Endless-Scroll: haengt die naechste Seite (offset = items.length) AN — server-
   *  gestreamt, jede nachgeladene Seite frisch vom Server. Via hasMore gegatet. */
  async loadMore(): Promise<void> {
    if (this._loadingMore || this._loading || !this.hasMore) return;
    const s = this.seq; // an die aktuelle Sequenz binden (loadMore bumpt seq NICHT)
    this._loadingMore = true;
    this.emit();
    try {
      const page = await this.fetcher(this.params(this._items.length));
      if (s === this.seq) {
        this._items = this._items.concat(page.items);
        this._total = page.total;
      }
      // stale (zwischenzeitlicher load/Filterwechsel) → Seite verwerfen, NICHT anhaengen
    } catch (e) {
      if (s === this.seq) {
        this._error = e;
        logger.warn("ListController.loadMore failed", e);
      }
    } finally {
      // _loadingMore ist das EIGENE Flag dieses loadMore → IMMER zuruecksetzen,
      // auch bei Sequenzwechsel (sonst bliebe der Infinite-Scroll dauerhaft haengen).
      this._loadingMore = false;
      this.emit();
    }
  }

  // --- Paginator-Ops (Seiten-Sicht auf den offset-Kern) ---
  firstPage(): Promise<void> {
    return this.goToOffset(0);
  }
  prevPage(): Promise<void> {
    return this.goToOffset(this._offset - this._limit);
  }
  nextPage(): Promise<void> {
    return this.goToOffset(this._offset + this._limit);
  }
  lastPage(): Promise<void> {
    return this.goToOffset((this.pageCount - 1) * this._limit);
  }
  goToPage(n: number): Promise<void> {
    return this.goToOffset((Math.max(1, n) - 1) * this._limit);
  }
  private goToOffset(off: number): Promise<void> {
    const max = (this.pageCount - 1) * this._limit;
    this._offset = Math.min(Math.max(0, off), Math.max(0, max));
    return this.load();
  }

  // --- Parameter-Setter (aendern Query → offset=0 → reload) ---
  setSearch(value: string): Promise<void> {
    const v = value ?? "";
    if (v === this._search) return Promise.resolve();
    this._search = v;
    this._offset = 0;
    return this.load();
  }
  setFilter(patch: Partial<F>): Promise<void> {
    this._filters = { ...this._filters, ...patch };
    this._offset = 0;
    return this.load();
  }
  setFilters(filters: F): Promise<void> {
    this._filters = filters;
    this._offset = 0;
    return this.load();
  }
  setSort(sort: SortKey[]): Promise<void> {
    this._sort = sort ?? [];
    this._offset = 0;
    return this.load();
  }
  /** Clampt die Seitengroesse auf den REST-Cap (PFS-627, sonst 422); WARN beim Clamp. */
  private clampLimit(n: number): number {
    const v = Math.max(1, Math.floor(n) || 1);
    if (v > this.restMaxLimit) {
      logger.warn(`ListController: limit ${v} > REST-Cap ${this.restMaxLimit} → geclampt (haette 422 ausgeloest)`);
      return this.restMaxLimit;
    }
    return v;
  }

  setLimit(limit: number): Promise<void> {
    const n = this.clampLimit(limit);
    if (n === this._limit) return Promise.resolve();
    this._limit = n;
    this._offset = 0;
    return this.load();
  }
  reset(): Promise<void> {
    this._search = "";
    this._sort = [];
    this._filters = this.initialFilters;
    this._offset = 0;
    return this.load();
  }
  /** Neu laden am aktuellen offset (z.B. nach einer Mutation). */
  reload(): Promise<void> {
    return this.load();
  }

  /** Optimistisches In-Place-Update OHNE Fetch (z.B. Membership-Flip): der
   *  mutator bekommt die aktuellen Items und liefert die neuen; totalDelta passt
   *  den Total an (z.B. -1 bei Zeilen-Entfernung). Emittiert danach. */
  patch(mutator: (items: T[]) => T[], totalDelta = 0): void {
    this._items = mutator(this._items.slice());
    this._total = Math.max(0, this._total + totalDelta);
    this.emit();
  }
}
